import { useEffect, useState } from "react"
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  onSnapshot, 
  addDoc, 
  deleteDoc,
  serverTimestamp,
  Timestamp,
  doc,
  getDoc
} from "firebase/firestore"
import { db } from "@/lib/firebase"
import { useAuthState } from "@/hooks/useAuth"
import type { Comment } from "@/types/models"
import { moderateComment } from "@/lib/contentModeration"

export function useComments(fotwFlashId: string) {
  const [comments, setComments] = useState<Comment[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [deleting, setDeleting] = useState<string | null>(null)
  const [userHasCommented, setUserHasCommented] = useState(false)
  const [userComment, setUserComment] = useState<Comment | null>(null)
  const { user } = useAuthState()

  useEffect(() => {
    if (!fotwFlashId) {
      setComments([])
      setLoading(false)
      return
    }

    const q = query(
      collection(db, "comments"),
      where("fotwFlashId", "==", fotwFlashId),
      orderBy("createdAt", "desc")
    )

    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        const commentsData = snapshot.docs.map(doc => {
          const data = doc.data()
          return {
            id: doc.id,
            comment: data.comment,
            commenter: data.commenter,
            fotwFlashId: data.fotwFlashId,
            userId: data.userId,
            createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(data.createdAt)
          } as Comment
        })
        setComments(commentsData)
        
        // Check if current user has already commented
        if (user) {
          const existingUserComment = commentsData.find(comment => comment.userId === user.uid)
          setUserHasCommented(!!existingUserComment)
          setUserComment(existingUserComment || null)
        }
        
        setLoading(false)
      },
      (err) => {
        console.error("Error fetching comments:", err)
        setError("Failed to load comments")
        setLoading(false)
      }
    )

    return () => unsubscribe()
  }, [fotwFlashId, user])

  const getUserDisplayName = async (userId: string): Promise<string> => {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId))
      if (userDoc.exists()) {
        const userData = userDoc.data()
        console.log('User data:', userData) // Debug log
        
        // Check for Instagram handle first, then email username, then fallback
        if (userData.instagram) {
          return `@${userData.instagram}`
        } else if (userData.email) {
          return userData.email.split('@')[0]
        }
      }
      return "Anonymous"
    } catch (error) {
      console.error('Error getting user display name:', error)
      return "Anonymous"
    }
  }

  const addComment = async (commentText: string) => {
    if (!user || !commentText.trim()) return false

    // Check if user has already commented
    if (userHasCommented) {
      setError("You can only comment once per flash")
      return false
    }

    // Moderate the comment
    const moderation = moderateComment(commentText.trim())
    if (!moderation.isAllowed) {
      setError(moderation.reason || "Comment not allowed")
      return false
    }

    setSubmitting(true)
    setError(null)

    try {
      const displayName = await getUserDisplayName(user.uid)
      
      await addDoc(collection(db, "comments"), {
        comment: moderation.cleanedText,
        commenter: displayName,
        fotwFlashId: fotwFlashId,
        userId: user.uid,
        createdAt: serverTimestamp()
      })
      return true
    } catch (err) {
      console.error("Error adding comment:", err)
      setError("Failed to add comment")
      return false
    } finally {
      setSubmitting(false)
    }
  }

  const deleteComment = async (commentId: string) => {
    if (!user) return false

    setDeleting(commentId)
    setError(null)

    try {
      await deleteDoc(doc(db, "comments", commentId))
      return true
    } catch (err) {
      console.error("Error deleting comment:", err)
      setError("Failed to delete comment")
      return false
    } finally {
      setDeleting(null)
    }
  }

  return { 
    comments, 
    loading, 
    error, 
    submitting, 
    deleting, 
    userHasCommented, 
    userComment,
    addComment, 
    deleteComment 
  }
} 